import { blowStep } from "./blow";
import {
  castleRightsMask,
  type Direction,
  KING,
  makePiece,
  PAWN,
  pieceColor,
  type PieceType,
  pieceType,
  rankOf,
  ROOK,
  square,
  STRIDE,
  WHITE,
  type Side
} from "../board";
import {
  isCastle,
  isDoublePush,
  isEnPassant,
  isStructureBlow,
  isStructureCapture,
  type Move,
  moveCapturedSide,
  moveCapturedType,
  moveFrom,
  movePromotion,
  moveTo,
  isBash,
  victimOf
} from "../move";
import { VARIANT_PLAIN } from "../rules";
import { applyDrag, undoDrag } from "./clasp";
import {
  captureStructureSquare,
  type Disguise,
  dropKing,
  facingAt,
  imitatingAt,
  crownedFrom as crownedRank,
  setCrowned,
  setDisguise,
  invalidateMoves,
  moveKing,
  pieceAt,
  type Position,
  passTurn,
  prevSide,
  promotionUsesAt,
  restoreKing,
  restoreStructureSquare,
  setFacing,
  setImitating,
  setPiece,
  setPromotionUses,
  setStructureHealth,
  structureHealthAt,
  obstructsAt,
  beltPartAt,
  variantAt,
  setVariant,
  hurtAt,
  setHurt,
  vitalityAt,
  setVitality,
  platingAt,
  setPlating,
  pieceIdAt,
  setPieceId,
  blowAgainst} from "../position";

/**
 * Irreversible state (castling rights, ep square, halfmove clock, the facings
 * of the mover and its victim) kept for unmake. The move itself carries
 * everything else.
 *
 * This used to be one packed 32-bit word, with the en passant square in eight
 * bits. A square is a packed coordinate now and needs twenty, and the whole
 * record no longer fits in a number JavaScript can do bitwise arithmetic on —
 * so it is a small object. It never escapes the make/unmake pair, which is the
 * case V8 is good at.
 */
export interface Undo {
  castling: number;
  ep: number;
  halfmove: number;
  facingFrom: Direction;
  facingVictim: Direction;
  /**
   * Which variant row moved, and which was taken. Saved for the facings'
   * reason: a search that unmade a move and left a plain pawn would have spent
   * the player's metal by thinking.
   */
  variantFrom: number;
  variantVictim: number;
  /**
   * The borrowed movement pattern of the mover and of its victim.
   *
   * Saved for exactly the reason the facings are: it belongs
   * to the *piece*, it decides what that piece can do, and a search that made
   * and unmade a move without putting it back would quietly hand a copied
   * pattern to whatever stood there next.
   */
  imitatingFrom: number;
  imitatingVictim: number;
  /** The rank the mover and the victim were crowned from, or 0. */
  crownedFrom: PieceType | 0;
  crownedVictim: PieceType | 0;
  /**
   * The costume the mover and its victim were wearing, or null (§26 C12).
   *
   * The crown's reason with the sharpest consequence: a costume is immunity
   * from capture, so a search that walked a spy forward and took the move back
   * would leave the immunity on a square the spy no longer stands on.
   */
  disguiseFrom: Disguise | null;
  disguiseVictim: Disguise | null;
  /**
   * Damage the mover and the victim had taken, and how tough each was.
   *
   * **The whole reason health moved onto `Position`** (§27 H1). Health decides
   * whether a capture is a capture, so move generation has to see it — and
   * `generateLegalMoves` sees a position by making and unmaking millions of
   * candidates. Without these two pairs a search that merely *considers* a
   * capture puts the victim back whole, so the board heals by being thought
   * about, and the piece it heals is the one somebody spent three turns
   * grinding down.
   */
  hurtFrom: number;
  hurtVictim: number;
  vitalityFrom: number;
  vitalityVictim: number;
  /** Armor each wore. No move spends any; a bash shoves, and a pool left behind is inherited. */
  platingFrom: number;
  platingVictim: number;
  /** Which body each was (`Position.pieceId`). A capture undone has to bring back the same one. */
  idFrom: number;
  idVictim: number;
  /**
   * Health the building on the victim's square had, or 0 for no building.
   *
   * The two pairs above, for a target with no piece on it. A search grinds a
   * warehouse down by *considering* moves against it without this — and a
   * captured one has to come back with the damage it already carried, which is
   * a property of the board and not of the move.
   */
  structureHealth: number;
  /** Whether the victim's square held an **obstruction** (§31 S1). */
  structureObstructs: boolean;
  /**
   * The non-obstruction structures this move destroyed (`move.broken`), as
   * owner and health, square-aligned — null when none. Unmake stands them back
   * up, or a search plows a real belt line flat by thinking about it.
   */
  brokenOwner: Side[] | null;
  brokenHealth: number[] | null;
  /** Which of `move.broken` were belt parts (§31 S2), square-aligned. */
  brokenBelt: boolean[] | null;
  /** Moves the side had left before this one, since the clock may not pass. */
  movesLeft: number;
  /** And its Fodder Moves (§33 T1): a flip refills them, so unmake puts them back. */
  fodderLeft: number;
  /**
   * Promotions the destination square had left *before* the move, when the
   * move spent one. Promotion squares are finite, and the search makes and
   * unmakes millions of moves — a use that was not handed back would drain
   * the board just by thinking about it.
   */
  promotionUses: number;
  /**
   * Which royal slot a captured king held, or -1 when nothing royal was taken.
   *
   * A side may hold more than one king (Coronation), so putting one back is no
   * longer "set the square" — the order decides which is the primary, and the
   * primary is what every "your king" reader means. `legalMoves` makes and
   * unmakes every candidate, so a slot restored wrong reorders a live army's
   * royalty by *thinking about* a capture.
   */
  royalSlot: number;
}

/** Rook from/to squares for a castle, keyed by the king's destination. */
function castleRookSquares(kingTo: number): [from: number, to: number] {
  const rank = rankOf(kingTo);
  return square(6, rank) === kingTo
    ? [square(7, rank), square(5, rank)]
    : [square(0, rank), square(3, rank)];
}

export function makeMove(pos: Position, move: Move): Undo {
  // The cached move list belonged to the board before this move. Every setter
  // below does this too, but make/unmake write `turn`, `ep` and `castling`
  // directly — so this is the one that has to be here rather than inherited.
  invalidateMoves(pos);
  const from = moveFrom(move);
  const to = moveTo(move);
  const piece = pieceAt(pos, from);
  const color = pieceColor(piece);
  const type = pieceType(piece);

  // Not always `to`. One reading, in `move.ts`, because this and its twin in
  // `unmakeMove` must not drift.
  const victim = victimOf(move, color);
  const promotionUses = promotionUsesAt(pos, to);
  const undo: Undo = {
    castling: pos.castling,
    ep: pos.ep,
    halfmove: pos.halfmove,
    facingFrom: facingAt(pos, from),
    facingVictim: facingAt(pos, victim),
    variantFrom: variantAt(pos, from),
    variantVictim: variantAt(pos, victim),
    imitatingFrom: imitatingAt(pos, from),
    imitatingVictim: imitatingAt(pos, victim),
    crownedFrom: crownedRank(pos, from),
    crownedVictim: crownedRank(pos, victim),
    // **The raw entry, not `disguisedAs`**, which answers null past the expiry:
    // `passTurn` advances the ply in here, so a costume can run out *during* a
    // make, and unmake has to restore what the map held rather than what the
    // rule saw.
    disguiseFrom: pos.disguise.get(from) ?? null,
    disguiseVictim: pos.disguise.get(victim) ?? null,
    hurtFrom: hurtAt(pos, from),
    hurtVictim: hurtAt(pos, victim),
    vitalityFrom: vitalityAt(pos, from),
    vitalityVictim: vitalityAt(pos, victim),
    platingFrom: platingAt(pos, from),
    platingVictim: platingAt(pos, victim),
    idFrom: pieceIdAt(pos, from),
    idVictim: pieceIdAt(pos, victim),
    structureHealth: structureHealthAt(pos, victim),
    structureObstructs: obstructsAt(pos, victim),
    brokenOwner: null,
    brokenHealth: null,
    brokenBelt: null,
    // Only when the move actually spends one: a piece merely passing over a
    // promotion square leaves it alone, and recording a use it never took
    // would put it back on unmake and drift the count upward.
    promotionUses: movePromotion(move) ? promotionUses : 0,
    movesLeft: pos.movesLeft,
    fodderLeft: pos.fodderLeft,
    royalSlot: -1
  };

  // **The plow** (§31 S1): every non-obstruction structure the move
  // destroys, out of the mirror alone — `applyMove` wrecks the industry's
  // copy for the move actually played.
  if (move.broken.length > 0) {
    const owners: Side[] = [];
    const health: number[] = [];
    const belts: boolean[] = [];
    for (const sq of move.broken) {
      owners.push(pos.structures.get(sq) ?? 0);
      health.push(structureHealthAt(pos, sq));
      belts.push(beltPartAt(pos, sq));
      captureStructureSquare(pos, sq);
    }
    undo.brokenOwner = owners;
    undo.brokenHealth = health;
    undo.brokenBelt = belts;
  }

  if (isEnPassant(move)) {
    setPiece(pos, victim, 0);
    setFacing(pos, victim, 0);
    setImitating(pos, victim, 0);
    setHurt(pos, victim, 0);
    setVitality(pos, victim, 0);
    setPlating(pos, victim, 0);
    setPieceId(pos, victim, 0);
    // Nothing below overwrites this square, so the immunity would outlive the
    // piece that wore it (§26 C12).
    setDisguise(pos, victim, null);
  }

  // **A blow that did not finish its target** (§27). Nothing is captured: the
  // target loses health and is driven a square along the blow, or stands its
  // ground while the attacker stops short of it. Its own path rather than a
  // clause in the one below, because two pieces move and one of them is not
  // the mover — every line after this assumes a single piece going `from` to
  // `to`, and one of the two may not move at all.
  if (isBash(move)) {
    // Read before anything moves: `blowOf` asks what is standing on `from`.
    // Less what a plow on the way spent (§31 S1).
    // Less the victim's Deflector Shields (§31 A9), never below nothing.
    const blow = Math.max(0, blowAgainst(pos, from, victim) - move.spent);
    // The shove landed exactly when the attacker took the target's square.
    // `bounceSquare` never answers `to`, so the two cases cannot be confused.
    const shoved = to === victim ? victim + blowStep(from, victim) : -1;

    if (shoved >= 0) {
      const driven = pieceAt(pos, victim);
      setPiece(pos, shoved, driven);
      setPiece(pos, victim, 0);
      // Everything that belongs to the piece goes with it, exactly as it does
      // for a mover — a shoved piece is still the same piece.
      setFacing(pos, shoved, undo.facingVictim);
      setFacing(pos, victim, 0);
      setVariant(pos, shoved, undo.variantVictim);
      setVariant(pos, victim, 0);
      setImitating(pos, shoved, undo.imitatingVictim as PieceType | 0);
      setImitating(pos, victim, 0);
      setCrowned(pos, shoved, undo.crownedVictim);
      setCrowned(pos, victim, 0);
      setDisguise(pos, shoved, undo.disguiseVictim);
      setDisguise(pos, victim, null);
      setVitality(pos, shoved, undo.vitalityVictim);
      setVitality(pos, victim, 0);
      setPlating(pos, shoved, undo.platingVictim);
      setPieceId(pos, shoved, undo.idVictim);
      setPlating(pos, victim, 0);
      setPieceId(pos, victim, 0);
      // A **spare** king can be shoved — a last one cannot be hurt at all —
      // and the royal list is by square, so it has to be told.
      if (pieceType(driven) === KING) {
        moveKing(pos, pieceColor(driven), victim, shoved);
      }
    }
    // The wound lands wherever the target ended up. It cannot be fatal: that
    // is what made this a bash rather than a capture.
    //
    // **A building takes it in its own map** (§27 + §26 F5). It is never
    // shoved — masonry does not move — so `shoved` is always -1 here and the
    // damage goes straight onto the square it stands on. Writing it into
    // `hurt` instead would put a wound on a square with no piece, which the
    // next piece to stand there would inherit.
    if (isStructureBlow(move)) {
      setStructureHealth(pos, victim, undo.structureHealth - blow);
    } else {
      setHurt(pos, shoved >= 0 ? shoved : victim, undo.hurtVictim + blow);
      if (shoved >= 0) setHurt(pos, victim, 0);
    }

    // The attacker. `from === to` is the ordinary way a knight bounces — it
    // has no approach to stop on — and clearing `from` after writing `to`
    // would delete it.
    if (from !== to) {
      setPiece(pos, to, piece);
      setPiece(pos, from, 0);
      setFacing(pos, to, undo.facingFrom);
      setFacing(pos, from, 0);
      setVariant(pos, to, undo.variantFrom);
      setVariant(pos, from, 0);
      setImitating(pos, to, undo.imitatingFrom as PieceType | 0);
      setImitating(pos, from, 0);
      setCrowned(pos, to, undo.crownedFrom);
      setCrowned(pos, from, 0);
      setDisguise(pos, to, undo.disguiseFrom);
      setDisguise(pos, from, null);
      setHurt(pos, to, undo.hurtFrom);
      setHurt(pos, from, 0);
      setVitality(pos, to, undo.vitalityFrom);
      setVitality(pos, from, 0);
      setPlating(pos, to, undo.platingFrom);
      setPieceId(pos, to, undo.idFrom);
      setPlating(pos, from, 0);
      setPieceId(pos, from, 0);
      if (type === KING) moveKing(pos, color, from, to);
    }

    // The clamped piece, after the attacker has left (§26 C11). A knight that
    // bounced never left, and `dragOf` answers -1 for it.
    applyDrag(pos, move);

    // Every square a piece left or arrived on, or a shoved rook keeps rights
    // it has walked away from.
    pos.castling &=
      castleRightsMask(from) &
      castleRightsMask(to) &
      castleRightsMask(victim) &
      (shoved >= 0 ? castleRightsMask(shoved) : ~0);
    pos.ep = -1;
    // Conservative, not provable, since a Miracle Healer puts health back: the
    // reset only delays a draw, and `repetitionKey` carries `hurt`.
    pos.halfmove = 0;
    passTurn(pos, color);
    return undo;
  }

  // **A captured structure's square becomes a cell again, with the mover on
  // it** (§26 F5). Position half only: the industry's copy is wrecked by
  // `applyMove`, never here — `legalMoves` makes and unmakes every candidate,
  // and a search must not empty a real warehouse by thinking about a move.
  if (isStructureCapture(move)) captureStructureSquare(pos, to);

  setPiece(pos, to, piece);
  setPiece(pos, from, 0);

  // Facing belongs to the piece, so it travels with it.
  setFacing(pos, to, undo.facingFrom);
  setFacing(pos, from, 0);
  // And the variant row, which is the same claim about the same kind of state:
  // a Marshal that walks a square is still a Marshal.
  setVariant(pos, to, undo.variantFrom);
  setVariant(pos, from, 0);
  // A copied pattern belongs to the piece, so it rides along — and the square
  // it left keeps nothing, or the next piece to stand there inherits it.
  setImitating(pos, to, undo.imitatingFrom as PieceType | 0);
  setImitating(pos, from, 0);
  // A crown belongs to the piece wearing it, so it rides along exactly as a
  // facing does — and the square she left keeps nothing, or the next piece to
  // stand there is an Empress by inheritance.
  setCrowned(pos, to, undo.crownedFrom);
  setCrowned(pos, from, 0);
  // A costume travels too, and is the one of these that walks into enemy ground
  // on purpose: left behind, the card is worth nothing (§26 C12).
  setDisguise(pos, to, undo.disguiseFrom);
  setDisguise(pos, from, null);
  // **Health belongs to the piece, so it travels** — or walking away from a
  // fight is how you heal. Writing `to` also overwrites whatever the victim
  // had, which is right: the victim is gone. The square left behind keeps
  // nothing, or the next piece to stand there inherits somebody's wounds.
  setHurt(pos, to, undo.hurtFrom);
  setHurt(pos, from, 0);
  setVitality(pos, to, undo.vitalityFrom);
  setVitality(pos, from, 0);
  // Armor rides along for the same reason, and is left behind for none.
  setPlating(pos, to, undo.platingFrom);
  setPieceId(pos, to, undo.idFrom);
  setPlating(pos, from, 0);
  setPieceId(pos, from, 0);

  const promotion = movePromotion(move);
  if (promotion) {
    setPiece(pos, to, makePiece(color, promotion));
    // A promotion square is spent by being *used*, whichever side used it,
    // and it goes entirely rather than counting down. A square you can march
    // a second pawn onto is a queen factory, and the scarcity is the point.
    if (promotionUses > 0) setPromotionUses(pos, to, 0);
    // Whatever it became, it is not a pawn and has no facing — and it is not
    // the piece that borrowed a pattern either.
    setFacing(pos, to, 0);
    setImitating(pos, to, 0);
    // **No longer a variant of the rank it has stopped being** — a pawn row
    // carried onto a queen would claim she is a kind of pawn. Armor survives a
    // promotion and this does not: a plate is worn, a variant is what it *is*.
    setVariant(pos, to, VARIANT_PLAIN);
  }

  if (isCastle(move)) {
    const [rookFrom, rookTo] = castleRookSquares(to);
    setPiece(pos, rookTo, pieceAt(pos, rookFrom));
    setPiece(pos, rookFrom, 0);
    // The rook's name too, on the same terms as its costume below.
    setPieceId(pos, rookTo, pieceIdAt(pos, rookFrom));
    setPieceId(pos, rookFrom, 0);
    // The rook's costume goes with it, or castling leaves an immunity on an
    // empty corner for whatever stands there next (§26 C12). No `Undo` slot:
    // both rook squares are distinct from `from` and `to`, so nothing here
    // overwrites them and the board is the truth on the way back.
    setDisguise(pos, rookTo, pos.disguise.get(rookFrom) ?? null);
    setDisguise(pos, rookFrom, null);
  }

  // **The clamped piece, once the mover's square is free** (§26 C11). It can
  // never collide with a castle's rook: `generateCastles` returns unless it is
  // called about the home square, where only a Monarch stands, and the card
  // refuses to clamp one.
  applyDrag(pos, move);

  // **By square, never by slot.** This used to be `pos.kings[color] = to`,
  // which is correct exactly while a side has one king and silently wrong the
  // moment it has two: whichever king moved last became *the* king, so moving
  // a spare stole royalty from the real one — and a check on the real king
  // then evaporated because the filter was asking about somewhere else.
  if (type === KING) moveKing(pos, color, from, to);
  // **A captured king leaves its side without one**, and this is the one thing
  // `removePiece` does beyond the board that `makeMove` has to do by hand: the
  // hot path writes through `setPiece`, which knows nothing about `kings`.
  //
  // Unreachable in standard chess, where a king is never a capture target — it
  // takes two kings carried adjacent by a belt (`canTake`) — so perft cannot
  // see this branch and `perft.test.ts` is what proves the counts are unmoved.
  //
  // A king is only a legal target when it is not its side's last (`canTake`),
  // or when two of them have been carried adjacent — so this takes one royal
  // off a list rather than emptying a slot, and the slot it held is what
  // unmake needs to put it back.
  if (moveCapturedType(move) === KING) {
    undo.royalSlot = dropKing(pos, moveCapturedSide(move), to);
  }

  pos.castling &= castleRightsMask(from) & castleRightsMask(to);
  pos.ep = isDoublePush(move) ? from + (color === WHITE ? STRIDE : -STRIDE) : -1;
  // A structure capture resets the clock too: the building is gone for good,
  // so the position can never repeat, exactly as after any other capture.
  pos.halfmove =
    type === PAWN || moveCapturedType(move) || isStructureCapture(move) || move.broken.length > 0
      ? 0
      : pos.halfmove + 1;
  passTurn(pos, color);

  return undo;
}

/**
 * Put the clock back, whatever kind of move it was.
 *
 * Shared by the ordinary restore and the bash one rather than written twice —
 * the halves easiest to leave out of a copy are `fullmove` and `ply`, and both
 * are silent when they drift: a ply count that runs slow expires armour and
 * socks late, and nothing throws.
 */
function restoreClock(pos: Position, undo: Undo, color: Side, passed: boolean): void {
  pos.castling = undo.castling;
  pos.ep = undo.ep;
  pos.halfmove = undo.halfmove;
  if (passed) {
    if (pos.turn === 0) pos.fullmove--;
    pos.turn = color;
  }
  pos.movesLeft = undo.movesLeft;
  pos.fodderLeft = undo.fodderLeft;
  pos.ply--;
}

export function unmakeMove(pos: Position, move: Move, undo: Undo): void {
  invalidateMoves(pos);
  const from = moveFrom(move);
  const to = moveTo(move);
  // Who moved. Not simply "the side before the one on the clock": with more
  // than one move to a turn the clock may not have passed at all, and then the
  // mover is still the side to move. `undo.movesLeft` is what says which.
  const passed = undo.movesLeft <= 1;
  const color = passed ? prevSide(pos, pos.turn) : pos.turn;
  const piece = pieceAt(pos, to);
  // **The same reading `makeMove` takes**, and it has to be: with the ordinary
  // one a bash whose shove was refused answers `to`, and `to === victim` is
  // exactly the test for "the shove landed" — so unmake would drive a piece
  // back that was never driven anywhere, dragging whatever stood beyond the
  // target onto the target's square. Found by driving it, and shared since.
  const victim = victimOf(move, color);

  // **The dragged piece comes home first** (§26 C11): its destination may be
  // the square the mover is about to return to. The clamp itself never moved —
  // `expireClasp` spends it, one move later, outside make/unmake.
  undoDrag(pos, move);

  // The bash, reversed (§27). Attacker first, then the target — when the shove
  // landed the attacker is standing on the square the target has to come back
  // to, so putting the target back first would be overwritten.
  if (isBash(move)) {
    const shoved = to === victim ? victim + blowStep(from, victim) : -1;

    if (from !== to) {
      setPiece(pos, from, piece);
      setPiece(pos, to, 0);
      setFacing(pos, from, undo.facingFrom);
      setFacing(pos, to, 0);
      setVariant(pos, from, undo.variantFrom);
      setVariant(pos, to, 0);
      setImitating(pos, from, undo.imitatingFrom as PieceType | 0);
      setImitating(pos, to, 0);
      setCrowned(pos, from, undo.crownedFrom);
      setCrowned(pos, to, 0);
      setDisguise(pos, from, undo.disguiseFrom);
      setDisguise(pos, to, null);
      setHurt(pos, from, undo.hurtFrom);
      setHurt(pos, to, 0);
      setVitality(pos, from, undo.vitalityFrom);
      setVitality(pos, to, 0);
      setPlating(pos, from, undo.platingFrom);
      setPieceId(pos, from, undo.idFrom);
      setPlating(pos, to, 0);
      setPieceId(pos, to, 0);
      if (pieceType(pieceAt(pos, from)) === KING) moveKing(pos, color, to, from);
    }

    if (shoved >= 0) {
      const driven = pieceAt(pos, shoved);
      setPiece(pos, victim, driven);
      setPiece(pos, shoved, 0);
      setVariant(pos, shoved, 0);
      setImitating(pos, shoved, 0);
      setCrowned(pos, shoved, 0);
      setDisguise(pos, shoved, null);
      setVitality(pos, shoved, 0);
      setPlating(pos, shoved, 0);
      setPieceId(pos, shoved, 0);
      setHurt(pos, shoved, 0);
      setFacing(pos, shoved, 0);
      if (pieceType(driven) === KING) {
        moveKing(pos, pieceColor(driven), shoved, victim);
      }
    }
    setFacing(pos, victim, undo.facingVictim);
    setVariant(pos, victim, undo.variantVictim);
    setImitating(pos, victim, undo.imitatingVictim as PieceType | 0);
    setCrowned(pos, victim, undo.crownedVictim);
    setDisguise(pos, victim, undo.disguiseVictim);
    // The wound the blow put on comes back off. This is the line a search
    // would otherwise leave behind: `legalMoves` makes and unmakes millions of
    // candidates, so a bash merely *considered* would grind the board down.
    setHurt(pos, victim, undo.hurtVictim);
    setVitality(pos, victim, undo.vitalityVictim);
    setPlating(pos, victim, undo.platingVictim);
    setPieceId(pos, victim, undo.idVictim);
    // And a building's, in its own map, for exactly that reason.
    if (isStructureBlow(move)) setStructureHealth(pos, victim, undo.structureHealth);

    restoreBroken(pos, move, undo);
    restoreClock(pos, undo, color, passed);
    return;
  }

  // A promotion put a new piece on `to`; the thing that moved was a pawn.
  setPiece(pos, from, movePromotion(move) ? makePiece(color, PAWN) : piece);
  if (undo.promotionUses > 0) setPromotionUses(pos, to, undo.promotionUses);
  setPiece(pos, to, 0);

  // The wall comes back where a structure capture was tried (§26 F5): the
  // square leaves `cells` again and the mirror names its owner, which the move
  // itself carries in `capturedSide` — `captured` is 0, so the piece-restore
  // branch below cannot fire for it.
  if (isStructureCapture(move)) {
    restoreStructureSquare(
      pos,
      to,
      moveCapturedSide(move),
      undo.structureHealth,
      undo.structureObstructs
    );
  }

  setFacing(pos, from, undo.facingFrom);
  setFacing(pos, to, 0);
  // Unconditional, because a promotion cleared the row — the board is not the
  // truth on the way back, only the `Undo` word is.
  setVariant(pos, from, undo.variantFrom);
  setVariant(pos, to, 0);
  setImitating(pos, from, undo.imitatingFrom as PieceType | 0);
  setImitating(pos, to, 0);
  setCrowned(pos, from, undo.crownedFrom);
  setCrowned(pos, to, 0);
  setDisguise(pos, from, undo.disguiseFrom);
  setDisguise(pos, to, null);
  setHurt(pos, from, undo.hurtFrom);
  setHurt(pos, to, 0);
  setVitality(pos, from, undo.vitalityFrom);
  setVitality(pos, to, 0);
  setPlating(pos, from, undo.platingFrom);
  setPieceId(pos, from, undo.idFrom);
  setPlating(pos, to, 0);
  setPieceId(pos, to, 0);

  const capturedType = moveCapturedType(move);
  if (capturedType) {
    const victimSide = moveCapturedSide(move);
    if (isEnPassant(move)) {
      setPiece(pos, victim, makePiece(victimSide, PAWN));
    } else {
      setPiece(pos, to, makePiece(victimSide, capturedType));
    }
    // And unmake puts the crown back where it was taken from. Without it a
    // search that tried the capture would leave the victim kingless for the
    // rest of the game — `legalMoves` makes and unmakes every candidate.
    if (capturedType === KING) restoreKing(pos, victimSide, to, undo.royalSlot);
  }
  setFacing(pos, victim, undo.facingVictim);
  setVariant(pos, victim, undo.variantVictim);
  setImitating(pos, victim, undo.imitatingVictim as PieceType | 0);
  setCrowned(pos, victim, undo.crownedVictim);
  setDisguise(pos, victim, undo.disguiseVictim);
  // The wounds the victim was carrying come back with it. Restored
  // unconditionally, like the four above: on a quiet move `victim` **is** `to`
  // and the recorded value is nought, so this is the same write twice.
  setHurt(pos, victim, undo.hurtVictim);
  setVitality(pos, victim, undo.vitalityVictim);
  setPlating(pos, victim, undo.platingVictim);
  setPieceId(pos, victim, undo.idVictim);

  if (isCastle(move)) {
    const [rookFrom, rookTo] = castleRookSquares(to);
    setPiece(pos, rookFrom, makePiece(color, ROOK));
    setPiece(pos, rookTo, 0);
    setPieceId(pos, rookFrom, pieceIdAt(pos, rookTo));
    setPieceId(pos, rookTo, 0);
    setDisguise(pos, rookFrom, pos.disguise.get(rookTo) ?? null);
    setDisguise(pos, rookTo, null);
  }

  if (pieceType(pieceAt(pos, from)) === KING) moveKing(pos, color, to, from);

  restoreBroken(pos, move, undo);
  restoreClock(pos, undo, color, passed);
}

/** Stand the plowed structures back up in the mirror (§31 S1). */
function restoreBroken(pos: Position, move: Move, undo: Undo): void {
  const owners = undo.brokenOwner;
  if (owners === null) return;
  for (let i = 0; i < move.broken.length; i++) {
    restoreStructureSquare(
      pos,
      move.broken[i],
      owners[i],
      undo.brokenHealth![i],
      false,
      undo.brokenBelt![i]
    );
  }
}
